import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { Accordion } from '@jouzen/ecom-components/v4/Accordion';
import { cx } from 'class-variance-authority';
import type { ReactNode, SourceHTMLAttributes } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

interface TwoColumnAccordionSectionProps {
  readonly dataCy?: string;
  readonly imageColStart: 'main' | 'full';
  readonly imageAltKey: MessageKey;
  readonly imageSrcSet: SourceHTMLAttributes<HTMLSourceElement>[];
  readonly title?: MessageKey;
  readonly copy?: MessageKey;
  readonly titleClassName?: string;
  readonly copyClassName?: string;
  readonly children: ReactNode;
}

const TwoColumnAccordionSection = ({
  dataCy,
  imageColStart,
  imageAltKey,
  imageSrcSet,
  title,
  copy,
  titleClassName,
  copyClassName,
  children,
}: TwoColumnAccordionSectionProps) => {
  return (
    <section className="bg-sandstone-200 pt-8 lg:pt-20" data-cy={dataCy}>
      <LayoutGrid rowGap={32} className="h-full">
        <GridItem
          className={cx('self-center overflow-hidden', {
            'aspect-square rounded-lg lg:mx-10 lg:aspect-5/6':
              imageColStart === 'main',
            'aspect-327/472 lg:aspect-square lg:max-h-192.5':
              imageColStart === 'full',
          })}
          colStart={{ sm: 'main', md: 6, lg: 'full' }}
          colEnd={{ sm: 'main', md: 20, lg: 13 }}
          rowStart={{ sm: 1 }}
        >
          <Motion
            className="relative h-full"
            initial={{ filter: 'blur(5px)', opacity: 0 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2, type: 'tween' }}
          >
            <ArtDirectionImage
              className={cx({
                'rounded-lg object-cover': imageColStart === 'main',
                'rounded-r-3xl object-cover max-lg:rounded-l-3xl':
                  imageColStart === 'full',
              })}
              fill
              localizedAlt={imageAltKey}
              priority
              sizes="100vw"
              srcSet={imageSrcSet}
            />
          </Motion>
        </GridItem>
        <GridItem
          className="flex flex-col justify-center p-0 lg:px-10"
          colStart={{ sm: 'main', md: 6, lg: 13 }}
          colEnd={{ sm: 'main', md: 20, lg: 'main' }}
          rowStart={{ sm: 2, lg: 1 }}
        >
          <Motion
            initial={{ filter: 'blur(5px)', opacity: 0 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2, type: 'tween' }}
          >
            {title && (
              <Typography
                variant="h4"
                height="tight"
                className={cx(
                  'mb-3 font-normal tracking-tighter text-balance md:text-wrap',
                  titleClassName,
                )}
              >
                <RichText messageKey={title} />
              </Typography>
            )}
            {copy && (
              <Typography
                variant="body-small"
                className={cx('mb-3', copyClassName)}
              >
                <RichText messageKey={copy} />
              </Typography>
            )}
            <Accordion defaultOpenIndex={0}>{children}</Accordion>
          </Motion>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default TwoColumnAccordionSection;
